import type {
  CapitalGlobalProposalAttachmentDocumentType,
  Proposal,
} from '@/services/proposals/types';

export type CapitalGlobalProposalInsuranceKind = 'new' | 'renewal';

export type CapitalGlobalProposalAttachmentSlot = {
  documentType: CapitalGlobalProposalAttachmentDocumentType;
  label: string;
  helperText: string;
  required?: boolean;
};

const PDF_MIME_TYPE = 'application/pdf';
const PDF_EXTENSION = '.pdf';

const MAX_ATTACHMENT_SIZE_MB = 10;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export const CAPITAL_GLOBAL_PROPOSAL_ATTACHMENT_ACCEPT = PDF_MIME_TYPE;

/** Matches Portal.Api buildPortalUploadObjectKey basename: {ms}-{uuid}-{file} */
const PORTAL_S3_UPLOAD_BASENAME =
  /^\d{13}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-(.+)$/i;

export function getCapitalGlobalProposalAttachmentDisplayFileName(
  fileName: string,
  keyPath?: string
): string {
  const basename =
    fileName.trim() || keyPath?.split('/').pop()?.trim() || '';
  if (!basename) return 'arquivo';
  const match = basename.match(PORTAL_S3_UPLOAD_BASENAME);
  return match?.[1] ?? basename;
}

const SIGNED_PROPOSAL_SLOT: CapitalGlobalProposalAttachmentSlot = {
  documentType: 'signed_proposal',
  label: 'Proposta de Seguro Assinada',
  helperText: 'Envie a proposta devidamente assinada em PDF.',
  required: true,
};

const BASE_SLOTS: CapitalGlobalProposalAttachmentSlot[] = [
  SIGNED_PROPOSAL_SLOT,
  {
    documentType: 'gfip',
    label: 'GFIP',
    helperText: 'Envie o documento GFIP em PDF.',
  },
];

const RENEWAL_SLOTS: CapitalGlobalProposalAttachmentSlot[] = [
  {
    documentType: 'claim_report',
    label: 'Relatório de Sinistro',
    helperText: 'Envie o relatório de sinistro em PDF.',
  },
  {
    documentType: 'invoice',
    label: 'Fatura',
    helperText: 'Envie a fatura em PDF.',
  },
];

export function resolveCapitalGlobalProposalInsuranceKind(
  proposal: Proposal
): CapitalGlobalProposalInsuranceKind {
  const typeRaw = proposal.policyType?.trim() || '1';
  return String(typeRaw) === '3' ? 'renewal' : 'new';
}

export function getCapitalGlobalProposalAttachmentSlots(
  insuranceKind: CapitalGlobalProposalInsuranceKind
): CapitalGlobalProposalAttachmentSlot[] {
  if (insuranceKind === 'renewal') {
    return [...BASE_SLOTS, ...RENEWAL_SLOTS];
  }
  return BASE_SLOTS;
}

function isPdfFile(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  return file.type === PDF_MIME_TYPE || lowerName.endsWith(PDF_EXTENSION);
}

export function validateCapitalGlobalProposalAttachmentFile(
  file: File
): string | undefined {
  if (!isPdfFile(file)) {
    return `Tipo de arquivo não permitido: ${file.name}. Envie apenas PDF (.pdf).`;
  }

  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return `O arquivo ${file.name} excede ${MAX_ATTACHMENT_SIZE_MB}MB.`;
  }

  return undefined;
}

export function hasCapitalGlobalSignedProposalAttachment(
  attachmentsByType: Partial<
    Record<
      CapitalGlobalProposalAttachmentDocumentType,
      { id: string } | undefined
    >
  >
): boolean {
  return Boolean(attachmentsByType.signed_proposal);
}
